import { motion } from "framer-motion";
import {
  Tablet, Truck, HardHat, Radio,
  Building2, Shield, Wifi, Globe, Phone, Satellite, Home,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  // Field Solutions
  {
    icon: Tablet,
    title: "Field Tablets & Mobile Internet",
    description: "Rugged tablets with nationwide cellular data so your crews stay productive on every job site.",
    link: "/field-tablets-mobile-internet",
  },
  {
    icon: Truck,
    title: "Fleet & Asset Tracking",
    description: "Real-time GPS tracking, geofencing, and live maps for vehicles, trailers, and equipment.",
    link: "/fleet-asset-tracking",
  },
  {
    icon: HardHat,
    title: "Jobsite Connectivity",
    description: "Temporary internet for trailers, remote sites, and pop-up locations — deployed in minutes.",
    link: "/jobsite-connectivity",
  },
  {
    icon: Radio,
    title: "Push-to-Talk",
    description: "Instant nationwide push-to-talk and dispatch that works across any cellular network.",
    link: "/push-to-talk",
  },
  // Enterprise Connectivity
  {
    icon: Building2,
    title: "Business Internet",
    description: "Enterprise-grade fiber, fixed wireless, and bonded internet with guaranteed SLAs and priority support.",
    link: "/business-internet-solutions",
  },
  {
    icon: Shield,
    title: "Failover Internet",
    description: "Instant hot failover with session persistence — keep operations running when your primary link drops.",
    link: "/failover-solutions",
  },
  {
    icon: Wifi,
    title: "Fixed Wireless Internet",
    description: "Multi-carrier 4G/5G connectivity deployed in 24–48 hours where wired ISPs can't reach.",
    link: "/fixed-wireless-solutions",
  },
  {
    icon: Globe,
    title: "IoT Connectivity",
    description: "Multi-IMSI global SIMs across 200+ countries and 800+ networks for any IoT deployment.",
    link: "/internet-of-things-iot",
  },
  {
    icon: Phone,
    title: "POTS Line Replacement",
    description: "Modern digital voice replacing legacy copper lines — UL2572, ASME, and NFPA certified.",
    link: "/pots-replacement-solutions",
  },
  {
    icon: Satellite,
    title: "Starlink Business",
    description: "Managed Starlink with commercial priority, static IPs, and integrated cellular failover.",
    link: "/starlink-business-internet",
  },
  // Home Internet
  {
    icon: Home,
    title: "Home Internet",
    description: "Truly unlimited 4G/5G home internet — no caps, no throttling, nationwide coverage in days.",
    link: "/home-internet",
  },
];

export const HomeServices = () => {
  return (
    <section id="services" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            All SimNet Solutions
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto font-medium">
            Field, enterprise, and home connectivity — all from one provider, one team, one bill.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-6 rounded-xl hover:shadow-xl hover:border-primary/40 transition-all group flex flex-col"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <service.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">{service.title}</h3>
              <p className="text-gray-300 mb-5 leading-relaxed flex-1">
                {service.description}
              </p>
              <a href={service.link} className="mt-auto">
                <Button variant="ghost" className="w-full justify-between group-hover:bg-primary/10">
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
